import React, { useState, useMemo, useRef, useEffect } from 'react';
import { usePlayerComparison } from '../../context/PlayerComparisonContext';
import PlayerComparisonTable from './PlayerComparisonTable';
import ComparisonVoteBarChart from '../VoteCharts/ComparisonVoteBarChart';
import ComparisonVoteProgressionChart from '../VoteCharts/ComparisonVoteProgressionChart';
import aflPlayerData from '../../assets/afl_player_data_2026.json';
import { Trash2 } from 'lucide-react';

const CHART_VIEWS = [
  { id: 'bars', label: 'Bars' },
  { id: 'progression', label: 'Progression' },
];

const PlayerComparisonGroup = ({ groupId, voteData, onRemove }) => {
  const { 
    getGroupPlayers, 
    addPlayerToGroup, 
    removePlayerFromGroup, 
    clearGroupPlayers 
  } = usePlayerComparison();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [chartView, setChartView] = useState('bars');
  const dropdownRef = useRef(null);
  
  // Get selected players for this group from context
  const selectedPlayers = getGroupPlayers(groupId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get all players for dropdown
  const allPlayers = useMemo(() => {
    if (!voteData?.leaderboard) return [];
    return voteData.leaderboard
      .map(player => ({
        name: player.name,
        team: player.team,
        fullTeamName: player.full_team_name
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [voteData?.leaderboard]);

  // Filter players based on search term
  const filteredPlayers = useMemo(() => {
    if (!searchTerm) return allPlayers;
    return allPlayers.filter(player =>
      player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.team.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allPlayers, searchTerm]);

  // Handle player selection - add to group if not already selected
  const handlePlayerSelect = (playerName) => {
    if (!selectedPlayers.includes(playerName)) {
      addPlayerToGroup(groupId, playerName);
    }
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  // Remove player from selection
  const removePlayer = (playerName) => {
    removePlayerFromGroup(groupId, playerName);
  };

  // Clear all selections
  const handleClearAllSelections = () => {
    clearGroupPlayers(groupId);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  return (
    <div className="bg-[#0a1120] border border-slate-800 rounded-2xl p-4 md:p-6 mb-6">
      {/* Two Column Layout: table gets the wider share so all columns fit without scrolling */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 xl:items-start">
        {/* First Column: Player Selection and Table */}
        <div className="flex flex-col h-full xl:col-span-3">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">Player Comparison</span>
            <div className="h-px flex-1 bg-slate-800/70" />
            {/* Delete sits inline with this header on stacked layouts */}
            {onRemove && (
              <button
                onClick={() => onRemove(groupId)}
                className="xl:hidden text-rose-400 hover:text-rose-300 transition-colors p-1 rounded-md border border-transparent hover:border-rose-500/30 hover:bg-rose-500/10"
                title="Remove this comparison group"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {/* Player Selection Dropdown */}
          <div className="mb-4">
            <div className="relative w-full" ref={dropdownRef}>
              <div className="relative">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search for a player..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                  className="bg-slate-900/50 border border-slate-700/60 rounded-lg pl-8 pr-10 h-10 text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/60 transition-colors w-full"
                />
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Dropdown Menu */}
              {isDropdownOpen && filteredPlayers.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-[#0f172a] border border-slate-700/60 rounded-xl shadow-2xl shadow-black/60 max-h-60 overflow-y-auto styled-scrollbar">
                  {filteredPlayers.map((player) => {
                    const playerImage = aflPlayerData.find(p => p.player_name === player.name)?.image_src;
                    return (
                      <button
                        key={player.name}
                        onClick={() => handlePlayerSelect(player.name)}
                        className={`w-full px-3 py-2 text-left hover:bg-slate-800/60 flex items-center space-x-3 transition-colors ${
                          selectedPlayers.includes(player.name) ? 'bg-emerald-500/10' : ''
                        }`}
                        disabled={selectedPlayers.includes(player.name)}
                      >
                        {playerImage ? (
                          <img 
                            src={playerImage} 
                            alt={player.name}
                            className="w-7 h-7 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                          />
                        ) : (
                          <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                            <span className="text-white font-bold text-xs">{player.name.charAt(0)}</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold truncate text-xs ${
                            selectedPlayers.includes(player.name) ? 'text-emerald-300' : 'text-white'
                          }`}>{player.name}</div>
                          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{player.team}</div>
                        </div>
                        {selectedPlayers.includes(player.name) && (
                          <svg className="w-3 h-3 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* No results message */}
              {isDropdownOpen && searchTerm && filteredPlayers.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-[#0f172a] border border-slate-700/60 rounded-xl shadow-2xl shadow-black/60">
                  <div className="px-3 py-2 text-slate-500 text-xs font-mono">
                    No players found matching "{searchTerm}"
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Comparison Table */}
          <div className="flex-1">
            <PlayerComparisonTable 
              selectedPlayers={selectedPlayers}
              voteData={voteData}
              onRemovePlayer={removePlayer}
              groupId={groupId}
            />
          </div>
        </div>

        {/* Second Column: Comparison Chart */}
        <div className="flex flex-col xl:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">Vote Visualization</span>
            <div className="h-px flex-1 bg-slate-800/70" />
            {/* Switches this card's chart between per-dataset bars and season progression */}
            <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-slate-950/60 border border-slate-800/70 flex-shrink-0">
              {CHART_VIEWS.map((view) => (
                <button
                  key={view.id}
                  onClick={() => setChartView(view.id)}
                  className={`px-2 h-6 text-[10px] font-mono uppercase tracking-[0.1em] rounded-md border transition-colors ${
                    chartView === view.id
                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                      : 'text-slate-500 border-transparent hover:text-slate-300 hover:bg-slate-800/60'
                  }`}
                  title={`Show ${view.label.toLowerCase()} chart`}
                >
                  {view.label}
                </button>
              ))}
            </div>
            {/* Delete sits inline at the card's top-right on side-by-side layouts */}
            {onRemove && (
              <button
                onClick={() => onRemove(groupId)}
                className="hidden xl:inline-flex text-rose-400 hover:text-rose-300 transition-colors p-1 rounded-md border border-transparent hover:border-rose-500/30 hover:bg-rose-500/10"
                title="Remove this comparison group"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex-1">
            {chartView === 'bars' ? (
              <ComparisonVoteBarChart 
                selectedPlayers={selectedPlayers}
                voteData={voteData}
              />
            ) : (
              <ComparisonVoteProgressionChart
                selectedPlayers={selectedPlayers}
                voteData={voteData}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 

export default PlayerComparisonGroup;